import axios from "axios";

// Cabinet Depot's deployed mobile backend — the single source of truth for
// orders / items / users / scans / Base44 sync. The desktop app is just a
// browser client on top of it.
//
// We hardcode this rather than rely on REACT_APP_BACKEND_URL because:
//   1. Emergent's platform sometimes auto-restores `frontend/.env` to a
//      preview default that points at the frontend itself.
//   2. Custom domains (e.g. warehouse.cabinetdepot.com) are deployed against
//      the same build, so an env var set to a single preview URL would break
//      every other domain that serves the same bundle.
// The env var is honoured ONLY for localhost dev.
//
// Test mode = same backend, `X-CD-Mode: test` header toggles the namespace.
// (This matches how Orders / Wallboard / Sync already work in Test mode.)
const FALLBACK_BACKEND = "https://picking-building-qa.emergent.host";

function resolveBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
  if (envUrl && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(envUrl)) {
    return envUrl;
  }
  return FALLBACK_BACKEND;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export const BACKEND_BASE = BACKEND_URL;

const api = axios.create({ baseURL: API });

// ---------------------------------------------------------------------------
// Token storage — single key, since we talk to a single backend and mode is
// just a header on that same backend.
// ---------------------------------------------------------------------------
const TOKEN_STORAGE_KEY = "cd_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_STORAGE_KEY); }
  catch (err) { return null; }
}
export function setStoredToken(token) {
  try { localStorage.setItem(TOKEN_STORAGE_KEY, token); }
  catch (err) { /* noop */ }
}
export function clearStoredToken() {
  try { localStorage.removeItem(TOKEN_STORAGE_KEY); }
  catch (err) { /* noop */ }
}

/** Decode JWT sub claim without verifying (just for reading the user id). */
function jwtUserId(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const padded = parts[1] + "=".repeat((-parts[1].length) & 3);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json)?.sub || null;
  } catch {
    return null;
  }
}

/** Resolve the desired X-CD-Mode header value. */
function resolveMode(token) {
  const fromCtx = typeof window !== "undefined" ? window.__cdActiveMode : null;
  if (fromCtx === "live" || fromCtx === "test") return fromCtx;
  const uid = jwtUserId(token);
  if (uid && typeof localStorage !== "undefined") {
    try {
      const stored = localStorage.getItem(`cd_view_mode_${uid}`);
      if (stored === "live" || stored === "test") return stored;
    } catch (err) { /* noop */ }
  }
  // Default for brand-new users — mirror ViewModeContext initial state.
  return token ? "live" : null;
}

api.interceptors.request.use((cfg) => {
  const token = getStoredToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const mode = resolveMode(token);
  if (mode) cfg.headers["X-CD-Mode"] = mode;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && window.location.pathname !== "/login") {
      clearStoredToken();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
